/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import {
  DASHBOARD_QUERY_GC_TIME,
  DASHBOARD_QUERY_STALE_TIMES,
  dashboardQueryKeys,
} from "@/dashboard/dashboardQueryConfig";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { formatIndicatorUnitLabel } from "@/libs/utils";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Settings } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";

const TableSkeleton = ({ columns = 4, rows = 3 }) => (
  <Table className="w-full border-collapse">
    <TableHeader>
      <TableRow>
        {[...Array(columns)].map((_, colIndex) => (
          <TableHead key={colIndex}>
            <Skeleton height={20} width="80%" />
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
    <TableBody>
      {[...Array(rows)].map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {[...Array(columns)].map((_, colIndex) => (
            <TableCell key={colIndex}>
              <Skeleton height={18} width="90%" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

type Indicator = {
  id: number;
  name: string;
  unity: string;
  esrs: string;
  cumulative: boolean | number;
  graphe: boolean | number;
  dashboard: boolean | number;
  programme: boolean | number;
  prog_id?: number | null;
  project?: string | null;
};

const ParameterIndicator = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "soustraitant" });
  const { user } = useAuth();
  const { postData } = usePostData<any>();
  const queryClient = useQueryClient();
  const hidePostActions = useHidePostActions();

  const [data, setData] = useState<Indicator[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [currentItem, setCurrentItem] = useState<Indicator | null>(null);

  const [cumulative, setCumulative] = useState(true);
  const [graphe, setGraphe] = useState(true);
  const [dashboard, setDashboard] = useState(true);
  const [programme, setProgramme] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [open, setOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchUnity, setSearchUnity] = useState("");
  const [searchEsrs, setSearchEsrs] = useState("");

  const nameSearchRef = useRef<HTMLInputElement>(null);
  const unitySearchRef = useRef<HTMLInputElement>(null);
  const esrsSearchRef = useRef<HTMLInputElement>(null);

  const endpoint = user
    ? `${import.meta.env.VITE_API_ENDPOINT}/getting/carriers/${
        user.id
      }/indicators`
    : "";

  const {
    data: indicators,
    loading,
  } = useFetchDataV2<Indicator[]>(
    endpoint,
    {
      browserCache: true,
      browserCacheKey: `parameter-indicators-${user?.id ?? "unknown"}`,
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: DASHBOARD_QUERY_STALE_TIMES.indicators,
      cacheId: dashboardQueryKeys.indicators,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.indicators,
      gcTime: DASHBOARD_QUERY_GC_TIME,
    },
  );

  const isIndicatorsQueryKey = (queryKey: readonly unknown[]) =>
    Array.isArray(queryKey) &&
    (
      queryKey[0] === dashboardQueryKeys.indicators ||
      queryKey[0] === `indicators-${user?.id}` ||
      (typeof queryKey[1] === "string" && queryKey[1] === endpoint)
    );

  const updateIndicatorInList = (
    indicatorsList: Indicator[] | undefined,
    updatedIndicator: Indicator,
  ) =>
    Array.isArray(indicatorsList)
      ? indicatorsList.map((item) =>
          item.id === updatedIndicator.id ? { ...item, ...updatedIndicator } : item,
        )
      : indicatorsList;

  useEffect(() => {
    if (indicators) setData(indicators);
  }, [indicators]);

  useEffect(() => {
    if (nameSearchRef.current) {
      setTimeout(() => nameSearchRef.current?.focus(), 100);
    }
  }, [searchName]);

  useEffect(() => {
    if (unitySearchRef.current) {
      setTimeout(() => unitySearchRef.current?.focus(), 100);
    }
  }, [searchUnity]);

  useEffect(() => {
    if (esrsSearchRef.current) {
      setTimeout(() => esrsSearchRef.current?.focus(), 100);
    }
  }, [searchEsrs]);

  const handleSave = async () => {
    if (!currentItem) return;
    const itemToUpdate = currentItem;

    const payload = {
      currentItem: {
        ...itemToUpdate,
        cumulative: Boolean(cumulative),
        graphe: Boolean(graphe),
        dashboard: Boolean(dashboard),
        programme: Boolean(programme),
      },
      cumulative: Boolean(cumulative),
      graphe: Boolean(graphe),
      dashboard: Boolean(dashboard),
      programme: Boolean(programme),
    };
    const updatedIndicator = payload.currentItem;
    const previousData = data;
    const cacheSnapshots = queryClient
      .getQueryCache()
      .findAll({ predicate: (query) => isIndicatorsQueryKey(query.queryKey) })
      .map((query) => ({
        queryKey: query.queryKey,
        data: queryClient.getQueryData(query.queryKey),
      }));

    setIsSaving(true);
    setOpen(false);
    setData((prevData) => updateIndicatorInList(prevData, updatedIndicator) ?? prevData);
    queryClient.setQueriesData(
      { predicate: (query) => isIndicatorsQueryKey(query.queryKey) },
      (previous: unknown) => updateIndicatorInList(previous as Indicator[] | undefined, updatedIndicator) ?? previous,
    );

    const res = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/parameter-indicators/crupdate`,
      data: payload,
    });

    if (!res) {
      setData(previousData);
      cacheSnapshots.forEach((snapshot) => {
        queryClient.setQueryData(snapshot.queryKey, snapshot.data);
      });
      setCurrentItem(itemToUpdate);
      setCumulative(Boolean(itemToUpdate.cumulative));
      setGraphe(Boolean(itemToUpdate.graphe));
      setDashboard(Boolean(itemToUpdate.dashboard));
      setProgramme(Boolean(itemToUpdate.programme));
      setOpen(true);
      setIsSaving(false);
      toast({
        title: "Erreur lors de la sauvegarde",
        description: "Les changements n’ont pas été enregistrés.",
        variant: "destructive",
      });
      return;
    }

    const savedIndicator = res?.currentItem ?? res?.indicator ?? res?.data ?? updatedIndicator;
    const finalIndicator = { ...updatedIndicator, ...savedIndicator };
    setData((prevData) => updateIndicatorInList(prevData, finalIndicator) ?? prevData);
    queryClient.setQueriesData(
      { predicate: (query) => isIndicatorsQueryKey(query.queryKey) },
      (previous: unknown) => updateIndicatorInList(previous as Indicator[] | undefined, finalIndicator) ?? previous,
    );
    setCurrentItem(finalIndicator);
    setIsSaving(false);
      toast({
        title: "Paramètres sauvegardés",
        description: "Les changements ont été enregistrés avec succès",
      });
  };

  const columns: ColumnDef<Indicator>[] = [
    {
      accessorKey: "name",
      header: "Nom",
      cell: ({ row }) => (
        <div>
          {row.getValue("name") +
            (row.original.project ? " (" + row.original.project + ")" : "")}
        </div>
      ),
    },
    {
      accessorKey: "unity",
      header: "Unité",
      cell: ({ row }) => formatIndicatorUnitLabel(row.original.unity),
    },
    { accessorKey: "esrs", header: "ESRS" },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => (
        // !hidePostActions && (
          <button
            type="button"
            onClick={() => {
              setCurrentItem(row.original);
              setCumulative(Boolean(row.original.cumulative));
              setGraphe(Boolean(row.original.graphe));
              setDashboard(Boolean(row.original.dashboard));
              setProgramme(Boolean(row.original.programme));
              setOpen(true);
            }}
            className="flex items-center gap-2 hover:text-blue-900 font-medium"
          >
            <Settings size={18} />
            Modifier
          </button>
        // )
      ),
    },
  ];

  const filteredData = useMemo(
    () =>
      data
        .filter((item) => item.prog_id === null)
        .filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
        ),
    [data]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: { sorting, columnFilters, columnVisibility, rowSelection },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
    },
  });

  const nameOptions = useMemo(
    () => Array.from(new Set(data.map((d) => d.name))),
    [data]
  );
  const unityOptions = useMemo(
    () => Array.from(new Set(data.map((d) => d.unity))),
    [data]
  );
  const esrsOptions = useMemo(
    () => Array.from(new Set(data.map((d) => d.esrs))),
    [data]
  );

  const filteredNameOptions = useMemo(
    () =>
      nameOptions.filter((option) =>
        option.toLowerCase().includes(searchName.toLowerCase())
      ),
    [nameOptions, searchName]
  );

  const filteredUnityOptions = useMemo(
    () =>
      unityOptions.filter((option) =>
        option.toLowerCase().includes(searchUnity.toLowerCase())
      ),
    [unityOptions, searchUnity]
  );

  const filteredEsrsOptions = useMemo(
    () =>
      esrsOptions.filter((option) =>
        option.toLowerCase().includes(searchEsrs.toLowerCase())
      ),
    [esrsOptions, searchEsrs]
  );

  return (
    <div>
      <ReactBreadcrumb
        items={[{ href: ``, name: "Paramètres Indicateurs" }]}
        isTruncated={false}
      />

      <div className="flex flex-wrap gap-5 items-center justify-between mt-5">
        <h1
          className="mb-2 mt-4 text-[24px] font-medium inline-block"
          id="program-title"
        >
          Paramètres Indicateurs
        </h1>
      </div>

      {/* --- Filtres --- */}
      <div className="flex flex-wrap gap-4 mb-4 mt-4">
        <Select
          onValueChange={(val) =>
            table.getColumn("name")?.setFilterValue(val === "all" ? "" : val)
          }
          onOpenChange={(open) => {
            if (!open) setSearchName("");
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtrer par Nom" />
          </SelectTrigger>
          <SelectContent>
            <div className="p-2 border-b">
              <Input
                ref={nameSearchRef}
                placeholder="Rechercher par Nom"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="mb-2"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
            <SelectItem value="all">Tous</SelectItem>
            {filteredNameOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          onValueChange={(val) =>
            table.getColumn("unity")?.setFilterValue(val === "all" ? "" : val)
          }
          onOpenChange={(open) => {
            if (!open) setSearchUnity("");
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtrer par Unité" />
          </SelectTrigger>
          <SelectContent>
            <div className="p-2 border-b">
              <Input
                ref={unitySearchRef}
                placeholder="Rechercher par Unité"
                value={searchUnity}
                onChange={(e) => setSearchUnity(e.target.value)}
                className="mb-2"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
            <SelectItem value="all">Toutes</SelectItem>
            {filteredUnityOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {formatIndicatorUnitLabel(opt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          onValueChange={(val) =>
            table.getColumn("esrs")?.setFilterValue(val === "all" ? "" : val)
          }
          onOpenChange={(open) => {
            if (!open) setSearchEsrs("");
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtrer par ESRS" />
          </SelectTrigger>
          <SelectContent>
            <div className="p-2 border-b">
              <Input
                ref={esrsSearchRef}
                placeholder="Rechercher par ESRS"
                value={searchEsrs}
                onChange={(e) => setSearchEsrs(e.target.value)}
                className="mb-2"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
            <SelectItem value="all">Tous</SelectItem>
            {filteredEsrsOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* --- Table --- */}
      <div className="w-full mt-5">
        <div className="rounded-md border">
          {loading && data.length === 0 ? (
            <TableSkeleton />
          ) : (
            <>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        {t("Pas de résultats.")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-muted-foreground">
                  {table.getRowModel().rows.length} of{" "}
                  {table.getFilteredRowModel().rows.length}
                </div>
                <div className="space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    {t("Précédent")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    {t("Suivant")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* --- Dialog --- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-auto">
          <DialogHeader>
            <DialogTitle>Paramètres de l'indicateur</DialogTitle>
            <DialogDescription>
              Gérer les options d'affichage et de calcul.
            </DialogDescription>
          </DialogHeader>

          {currentItem && (
            <div className="grid grid-cols-5 gap-6 mt-6">
              <div className="col-span-3 space-y-4">
                <div>
                  <Label>Nom :</Label>
                  <Input value={currentItem.name} disabled />
                </div>
                <div>
                  <Label>Unité :</Label>
                  <Input value={formatIndicatorUnitLabel(currentItem.unity)} disabled />
                </div>
                <div>
                  <Label>ESRS :</Label>
                  <Input value={currentItem.esrs} disabled />
                </div>
              </div>

              <div className="col-span-2 space-y-4">
                <div className="border rounded-lg p-3 flex items-center justify-between">
                  <Label>Afficher sur le tableau de bord :</Label>
                  <Switch checked={dashboard} onCheckedChange={setDashboard} />
                </div>
                <div className="border rounded-lg p-3 flex items-center justify-between">
                  <Label>Afficher sur la fiche programme :</Label>
                  <Switch checked={programme} onCheckedChange={setProgramme} />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 border-t p-4">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
              Annuler
            </Button>
            {/* {!hidePostActions && ( */}
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Sauvegarde..." : "Sauvegarder"}
              </Button>
            {/* )} */}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParameterIndicator;
